import { useCallback, useEffect, useRef, useState, type JSX } from 'react';
import { getUserListsPreview, validateToken } from '@/services/slaydAPI';
import useSlaylistSelectorStore from '@/stores/SlaylistSelectorStore';
import { AuthContext } from './AuthContext';
import { SESSION_KEYS } from './AuthContext.constants';
import type { AuthContextType, AuthProviderProps } from './AuthContext.types';

// Generate a unique session ID
const generateSessionId = (): string =>
  `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

/**
 * AuthProvider - Manages authentication state and session data
 *
 * Features:
 * - Validates user authentication on session start
 * - Fetches and caches slaylists in session storage
 * - Prevents unnecessary API calls by checking session storage first
 * - Handles login/logout state changes
 */
export const AuthProvider = ({ children }: AuthProviderProps): JSX.Element => {
  const [signed, setSigned] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const hasValidatedRef = useRef(false);
  const currentSessionId = useRef<string>('');

  // Initialize session
  useEffect(() => {
    const existingSessionId = sessionStorage.getItem(SESSION_KEYS.SESSION_ID);
    if (!existingSessionId) {
      // New session - generate new ID and clear any stale data
      currentSessionId.current = generateSessionId();
      sessionStorage.setItem(SESSION_KEYS.SESSION_ID, currentSessionId.current);
      sessionStorage.removeItem(SESSION_KEYS.AUTH_VALIDATED);
      sessionStorage.removeItem(SESSION_KEYS.AUTH_STATE);
      // Don't clear slaylists on new session - let the store handle its own persistence
    } else {
      currentSessionId.current = existingSessionId;
    }
  }, []);

  // Function to fetch and store slaylists
  const fetchAndStoreSlaylists = async (): Promise<void> => {
    try {
      const slaylists = await getUserListsPreview();
      if (slaylists) {
        // Update the store directly - let the store handle its own persistence
        useSlaylistSelectorStore.getState().setSlaylists(slaylists);
      }
    } catch (error) {
      console.error('Failed to fetch slaylists at session start:', error);
    }
  };

  // Function to load slaylists from session storage
  const loadSlaylistsFromSession = useCallback((): void => {
    // Let the SlaylistSelectorStore handle its own persistence
    // The store will automatically load from session storage on initialization
    const store = useSlaylistSelectorStore.getState();
    const existingSlaylists = store.getSlaylists();

    // Only fetch if we don't have slaylists already
    if (!existingSlaylists || existingSlaylists.length === 0) {
      fetchAndStoreSlaylists();
    }
  }, []);

  const checkAuth = useCallback(async (): Promise<void> => {
    // Check if validation already happened in this session
    const hasValidatedInSession =
      sessionStorage.getItem(SESSION_KEYS.AUTH_VALIDATED) === 'true';

    if (hasValidatedRef.current || hasValidatedInSession) {
      // If already validated, restore state from session storage
      if (hasValidatedInSession && !hasValidatedRef.current) {
        const savedAuthState = sessionStorage.getItem(SESSION_KEYS.AUTH_STATE);
        setSigned(savedAuthState === 'true');
        setIsInitialized(true);
        setIsLoading(false);
        hasValidatedRef.current = true;

        // Load slaylists from session storage if user is authenticated
        if (savedAuthState === 'true') {
          loadSlaylistsFromSession();
        }
      }
      return;
    }

    try {
      setIsLoading(true);
      hasValidatedRef.current = true;

      const result = await validateToken();
      setSigned(result.success);
      setIsInitialized(true);

      // Save validation state to session storage
      sessionStorage.setItem(SESSION_KEYS.AUTH_VALIDATED, 'true');
      sessionStorage.setItem(
        SESSION_KEYS.AUTH_STATE,
        result.success.toString()
      );

      // If user is authenticated, fetch slaylists
      if (result.success) {
        await fetchAndStoreSlaylists();
      }
    } catch (error) {
      console.error('Auth validation error:', error);
      setSigned(false);
      setIsInitialized(true);

      // Save failed state to session storage
      sessionStorage.setItem(SESSION_KEYS.AUTH_VALIDATED, 'true');
      sessionStorage.setItem(SESSION_KEYS.AUTH_STATE, 'false');
    } finally {
      setIsLoading(false);
    }
  }, [loadSlaylistsFromSession]);

  // Force revalidation (useful for logout or token refresh scenarios)
  const forceRevalidate = async (): Promise<void> => {
    hasValidatedRef.current = false;
    sessionStorage.removeItem(SESSION_KEYS.AUTH_VALIDATED);
    sessionStorage.removeItem(SESSION_KEYS.AUTH_STATE);
    // Don't clear slaylists - let the store handle its own persistence
    await checkAuth();
  };

  // Custom setSigned that also updates session storage
  const setSignedWithSession = (newSignedState: boolean): void => {
    setSigned(newSignedState);
    sessionStorage.setItem(SESSION_KEYS.AUTH_STATE, newSignedState.toString());

    // If user is signing in, fetch slaylists
    if (newSignedState) {
      fetchAndStoreSlaylists();
    } else {
      // If user is signing out, let the store handle clearing its own data
      useSlaylistSelectorStore.getState().setSlaylists([]);
    }
  };

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const value: AuthContextType = {
    signed,
    setSigned: setSignedWithSession,
    isLoading,
    isInitialized,
    checkAuth,
    forceRevalidate,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
